import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Lock } from "lucide-react";

import {
  planCatalogQueryKeys,
  planScreenApi,
  planScreenQueryKeys,
  type PlanCatalogRow,
  type ScreenCatalog,
  type ScreenGroup,
  type ScreenItem,
} from "../../shared/api/platformBilling";
import { apiError } from "../../shared/api/client";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import {
  FeatureBrowser,
  FeatureName,
  type FeatureEntry,
  type FeatureFilter,
} from "./FeatureBrowser";
import { activeAdvisories, allScreens, tick, untick } from "./planScreenRules";

const WARNING_BOX =
  "rounded-md border border-amber-300 bg-amber-50 p-2 text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200";

/** Hộp thoại chọn tính năng của một gói: tiêu đề đứng yên, danh sách cuộn, thanh lưu luôn thấy. */
export function PlanScreensDialog({
  plan,
  onClose,
}: {
  plan: PlanCatalogRow;
  onClose: () => void;
}) {
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="flex h-[85vh] max-w-5xl flex-col gap-4">
        <DialogHeader>
          <DialogTitle>Tính năng của gói</DialogTitle>
          <DialogDescription>
            {plan.name}
            {plan.tenantsOnPlan > 0 &&
              ` · ${plan.tenantsOnPlan} phòng khám đang dùng`}
          </DialogDescription>
        </DialogHeader>
        <PlanScreensPanel plan={plan} onClose={onClose} />
      </DialogContent>
    </Dialog>
  );
}

/** Trạng thái một tính năng trong bản nháp: đang chọn không, và đã lưu là gì. */
interface Tick {
  on: boolean;
  saved: boolean;
}

const FILTERS: FeatureFilter<Tick>[] = [
  {
    key: "off",
    label: "Chưa chọn",
    title: "Chưa chọn",
    match: (e) => !e.state.on,
  },
  {
    key: "dirty",
    label: "Đang sửa",
    title: "Đang sửa, chưa lưu",
    tone: "amber",
    match: (e) => e.state.on !== e.state.saved,
  },
];

/**
 * Tick những màn hình một gói gồm (ADR-0053). Trên giao diện, mỗi màn hình là một "tính năng".
 *
 * <p>Danh sách màn hình, màn hình nào bị khoá và màn hình nào kéo theo màn hình nào đều đến từ máy
 * chủ; bảng này không tự biết tên một màn hình nào.
 *
 * <p><b>Bỏ tick chỉ ẩn, không xoá.</b> Dữ liệu phòng khám đã tạo vẫn nguyên và tick lại là thấy
 * lại. Vì vậy thứ cần chặn tay không phải mất dữ liệu mà là thay đổi âm thầm: gói đang có phòng
 * khám dùng thì lần lưu phải qua một bước xác nhận nói rõ bao nhiêu phòng khám bị ảnh hưởng.
 *
 * <p>Thanh dưới cùng luôn thấy, và chỉ nói về <em>lần lưu sắp tới</em>: tóm tắt thêm/bỏ, cảnh báo
 * về tổ hợp sắp lưu, bước xác nhận. Chưa sửa gì thì thanh ấy im — một cảnh báo hiện thường trực
 * về tập đã lưu là cảnh báo không ai còn đọc.
 */
export function PlanScreensPanel({
  plan,
  onClose,
}: {
  plan: PlanCatalogRow;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  // null = chưa sửa gì, đang hiển thị đúng tập đã lưu.
  const [draft, setDraft] = useState<Set<string> | null>(null);
  const [confirming, setConfirming] = useState(false);

  const catalogQ = useQuery({
    queryKey: planScreenQueryKeys.catalog(),
    queryFn: () => planScreenApi.catalog(),
    staleTime: Infinity,
  });
  const ticksQ = useQuery({
    queryKey: planScreenQueryKeys.ticks(plan.id),
    queryFn: () => planScreenApi.ticks(plan.id),
  });

  const save = useMutation({
    mutationFn: (screens: string[]) => planScreenApi.tick(plan.id, screens),
    onSuccess: (saved) => {
      qc.setQueryData(planScreenQueryKeys.ticks(plan.id), saved);
      void qc.invalidateQueries({ queryKey: planCatalogQueryKeys.all() });
      setDraft(null);
      setConfirming(false);
    },
  });

  const catalog = catalogQ.data;
  const saved = ticksQ.data;
  if (catalogQ.isError || ticksQ.isError) {
    return (
      <p className="text-sm text-destructive">
        {apiError(catalogQ.error ?? ticksQ.error).detail}
      </p>
    );
  }
  if (!catalog || !saved) {
    return (
      <p className="text-sm text-muted-foreground">
        Đang tải tính năng của gói…
      </p>
    );
  }

  const savedSet = new Set(saved);
  const ticked = draft ?? savedSet;
  const screens = allScreens(catalog);
  const labelOf = new Map(screens.map((s) => [s.code, s.label]));
  const labels = (codes: string[]) =>
    codes.map((c) => labelOf.get(c) ?? c).join(", ");
  // Màn hình không có đường dẫn riêng (không `path` — một tab bên trong màn hình khác) không có
  // dòng "Kéo theo": thứ nó kéo theo là trang chứa nó, hiển nhiên từ tên; bộ tick vẫn tự thêm khi lưu.
  const pulledInBy = (screen: ScreenItem) =>
    screen.path ? labels(screen.requires) : "";
  const tickedCount = screens.filter((s) => ticked.has(s.code)).length;
  const added = [...ticked].filter((code) => !savedSet.has(code));
  const removed = saved.filter((code) => !ticked.has(code));
  const dirty = added.length > 0 || removed.length > 0;
  const affectsClinics = plan.tenantsOnPlan > 0;

  function change(next: Set<string> | null) {
    setDraft(next);
    setConfirming(false);
    save.reset();
  }

  const set = (code: string, on: boolean) =>
    change(on ? tick(catalog!, ticked, code) : untick(catalog!, ticked, code));

  function submit() {
    if (affectsClinics && !confirming) {
      setConfirming(true);
      return;
    }
    save.mutate([...ticked]);
  }

  return (
    <section
      className="flex min-h-0 flex-1 flex-col gap-4"
      aria-label={`Tính năng của gói ${plan.name}`}
    >
      <FeatureBrowser<Tick>
        catalog={catalog}
        stateOf={(code) => ({
          on: ticked.has(code),
          saved: savedSet.has(code),
        })}
        isOn={(s) => s.on}
        filters={FILTERS}
        hint="Tick là phòng khám ở gói này thấy trong menu. Bỏ tick chỉ ẩn, dữ liệu đã có không bị xoá."
        layout="grid"
        onNavigate={() => setConfirming(false)}
        renderGroupTools={(group, entries) => (
          <GroupToggle
            catalog={catalog}
            group={group}
            entries={entries}
            ticked={ticked}
            onChange={change}
          />
        )}
        renderRow={({ screen, state }, groupLabel) => (
          <ScreenRow
            key={screen.code}
            screen={screen}
            state={state}
            groupLabel={groupLabel}
            requiresLabel={pulledInBy(screen)}
            onChange={(on) => set(screen.code, on)}
          />
        )}
      />

      <footer className="space-y-3">
        {dirty &&
          activeAdvisories(catalog, ticked).map((a) => (
            <p
              key={a.screen}
              role="alert"
              className={cn(WARNING_BOX, "text-xs")}
            >
              {a.message}
            </p>
          ))}

        {confirming && (
          <p role="alert" className={cn(WARNING_BOX, "text-sm")}>
            <b>{plan.tenantsOnPlan} phòng khám</b> đang ở gói này và sẽ thấy
            thay đổi ngay. Bấm lần nữa để xác nhận.
          </p>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="min-w-0 text-xs text-muted-foreground">
            <span className="tabular-nums">
              Đã chọn {tickedCount}/{screens.length} tính năng.
            </span>
            {added.length > 0 && (
              <>
                {" "}
                <b className="text-foreground">Thêm:</b> {labels(added)}.
              </>
            )}
            {removed.length > 0 && (
              <>
                {" "}
                <b className="text-foreground">Bỏ:</b> {labels(removed)}.
              </>
            )}
          </p>
          <div className="flex items-center gap-2">
            {save.isError && (
              <span className="text-sm text-destructive">
                {apiError(save.error).detail}
              </span>
            )}
            {dirty ? (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => change(null)}
              >
                Bỏ thay đổi
              </Button>
            ) : (
              <Button type="button" size="sm" variant="ghost" onClick={onClose}>
                Đóng
              </Button>
            )}
            <Button
              type="button"
              size="sm"
              disabled={!dirty || save.isPending}
              onClick={submit}
            >
              {save.isPending
                ? "Đang lưu…"
                : confirming
                  ? `Xác nhận lưu cho ${plan.tenantsOnPlan} phòng khám`
                  : "Lưu tính năng"}
            </Button>
          </div>
        </div>
      </footer>
    </section>
  );
}

/** Ô tick cho cả nhóm đang xem: tick đủ thì bỏ hết (màn hình khoá tự ở lại), còn lại thì tick đủ. */
function GroupToggle({
  catalog,
  group,
  entries,
  ticked,
  onChange,
}: {
  catalog: ScreenCatalog;
  group: ScreenGroup;
  entries: FeatureEntry<Tick>[];
  /** Toàn bộ tập đang tick, mọi nhóm — nhóm này chỉ là phần được sửa. */
  ticked: ReadonlySet<string>;
  onChange: (next: Set<string>) => void;
}) {
  const on = entries.filter((e) => e.state.on).length;
  const state =
    on === 0 ? false : on === entries.length ? true : "indeterminate";

  return (
    <label className="flex shrink-0 items-center gap-2 text-xs font-medium">
      <Checkbox
        aria-label={group.label}
        checked={state}
        onCheckedChange={() => {
          const apply = state === true ? untick : tick;
          onChange(
            entries.reduce<Set<string>>(
              (acc, e) => apply(catalog, acc, e.screen.code),
              new Set(ticked),
            ),
          );
        }}
      />
      Cả nhóm
    </label>
  );
}

function ScreenRow({
  screen,
  state,
  groupLabel,
  requiresLabel,
  onChange,
}: {
  screen: ScreenItem;
  state: Tick;
  groupLabel?: string;
  /** Tên các tính năng được kéo theo khi bật, rỗng nếu không có. */
  requiresLabel: string;
  onChange: (on: boolean) => void;
}) {
  const changed = state.on !== state.saved;
  return (
    <li>
      {/* Cả dòng là nhãn của ô tick: bấm vào tên cũng tick, không phải nhắm vào ô nhỏ. */}
      <label
        className={cn(
          "flex items-start gap-2 rounded-md px-2 py-1.5",
          screen.locked
            ? "cursor-default"
            : "cursor-pointer hover:bg-accent/40",
        )}
      >
        <Checkbox
          className="mt-0.5"
          aria-label={screen.label}
          checked={state.on}
          disabled={screen.locked}
          onCheckedChange={(checked) => onChange(checked === true)}
        />
        <div className="min-w-0 flex-1">
          <FeatureName label={screen.label} groupLabel={groupLabel} />
          {screen.locked ? (
            <p
              className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground"
              title="Tính năng nền của hệ thống: có sẵn trong mọi gói, không bỏ tick được."
            >
              <Lock className="h-3 w-3" /> Tính năng nền · luôn bật
            </p>
          ) : (
            (changed || requiresLabel) && (
              <p className="mt-0.5 flex flex-wrap gap-x-3 text-xs">
                {changed && (
                  <span className="font-medium text-amber-600">
                    {state.on ? "Sẽ thêm" : "Sẽ bỏ"} khi lưu
                  </span>
                )}
                {requiresLabel && (
                  <span className="text-muted-foreground">
                    Kéo theo: {requiresLabel}
                  </span>
                )}
              </p>
            )
          )}
        </div>
      </label>
    </li>
  );
}
